import { useDebouncedValue } from "../hooks"

type QueryFunction<T> = (text: string) => T[]
type AsyncQueryFunction<T> = (text: string) => PromiseLike<T[]>

interface SharedQuery<T> {

    /** Indicates the filter should be applied */
    readonly applyFilter: boolean

    /** Item currently selected */
    readonly selected: T

}

export type Query<T> = SharedQuery<T> & (
    | {
        items: T[]
        textSelector: (item: T) => string
    }
    | {
        query: QueryFunction<T>
    }
    | {
        queryAsync: AsyncQueryFunction<T>
        debounce: number
    }
)

type ArrayQuery<T> = Extract<Query<T>, { items: T[] }>
type AsyncQuery<T> = Extract<Query<T>, { queryAsync: AsyncQueryFunction<T> }>
type SyncQuery<T> = Extract<Query<T>, { query: QueryFunction<T> }>

export function useQuery<T>(query: Query<T>, text: string) {

    const [result, setResult] = React.useState<T[] | undefined>()

    const debounce = isAsyncQuery(query)
        ? query.debounce
        : 0

    text = useDebouncedValue(text, debounce)

    React.useEffect(() => {

        if (isArrayQuery(query)) {
            setResult(queryArray(query, text))
            return
        }

        if (isAsyncQuery(query)) {
            return queryAsync(query, text, setResult)
        }

        if (isSyncQuery(query)) {
            setResult(querySync(query, text))
        }

        setResult([])

    }, [query, text])

    return result

}


function queryArray<T>({ items, textSelector, selected, applyFilter }: ArrayQuery<T>, text: string) {

    if (!(text && applyFilter)) {
        return items
    }

    const re = new RegExp(escape(text), "i")

    return items.filter(s => re.test(textSelector(s)))

}

function querySync<T>({ query, selected, applyFilter }: SyncQuery<T>, text: string) {

    if (!(text && applyFilter)) {
        return query("")
    }

    return query(text)

}

function queryAsync<T>({ queryAsync, selected, applyFilter }: AsyncQuery<T>, text: string, setResult: (result: T[]) => void) {

    let ignore = false

    run().then(result => {

        if (!ignore) {
            setResult(result)
        }

    })

    return () => {
        ignore = true
    }

    function run() {

        if (!(text && applyFilter)) {
            return queryAsync("")
        }

        return queryAsync(text)

    }

}


function isArrayQuery<T>(arg: Query<T>): arg is ArrayQuery<T> {

    if ((arg as ArrayQuery<T>).items) {
        return true
    }

    return false

}

function isAsyncQuery<T>(arg: Query<T>): arg is AsyncQuery<T> {

    if ((arg as AsyncQuery<T>).queryAsync) {
        return true
    }

    return false

}

function isSyncQuery<T>(arg: Query<T>): arg is SyncQuery<T> {

    if ((arg as SyncQuery<T>).query) {
        return true
    }

    return false

}

export function filterArrayIgnoreCase<T>(items: T[], text: string, textSelector: (item: T) => string) {

    if (!text) {
        return items
    }

    const re = new RegExp(escape(text), "i")

    return items.filter(s => re.test(textSelector(s)))

}
